/**
 * Window options + patch helpers for the tag-rule compare editor. The window
 * select speaks three states: "same as the rule window" (no preset stored),
 * a named preset, or custom bounds — mapped onto TagCompareTo's fields.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { TagCompareTo } from '@trafficpulse/shared';

import {
  STATS_DATE_RANGE_PRESETS,
  getStatsDateRangeLabel,
} from '@/components/date-range';

/** Select value for the inherit state (nothing stored on the condition). */
export const SAME_WINDOW = '__same-window__';

export interface WindowOption {
  value: string;
  label: string;
}

export function compareWindowOptions(excluded: readonly string[] = []): WindowOption[] {
  return [
    { value: SAME_WINDOW, label: 'Same as rule window' },
    ...STATS_DATE_RANGE_PRESETS.filter(
      (p) => p.code !== 'custom' && !excluded.includes(p.code),
    ).map((p) => ({
      value: p.code,
      label: getStatsDateRangeLabel(p.code),
    })),
    { value: 'custom', label: 'Custom…' },
  ];
}

/** Same metric, same window, NO percent — the direct-comparison start. */
export function defaultCompareTo(field: string): TagCompareTo {
  return { field };
}

export function windowValueOf(ct: TagCompareTo): string {
  return ct.dateRangePreset ?? SAME_WINDOW;
}

/** Apply a window select change, keeping custom bounds only while custom.
 *  Rebuilds drop stale window keys but must carry every non-window key. */
export function withWindow(ct: TagCompareTo, value: string): TagCompareTo {
  if (value === SAME_WINDOW) {
    return { field: ct.field, percent: ct.percent, against: ct.against };
  }
  if (value !== 'custom') {
    return {
      field: ct.field,
      percent: ct.percent,
      against: ct.against,
      dateRangePreset: value,
    };
  }
  return {
    ...ct,
    dateRangePreset: 'custom',
    fromDate: ct.fromDate ?? '',
    toDateIsToday: ct.toDateIsToday ?? true,
  };
}

/** Short human summary — "Revenue (Last 7 days)" / "Revenue (rule window)". */
export function describeCompareTo(
  ct: TagCompareTo,
  labelOf: (key: string) => string,
): string {
  const win = ct.dateRangePreset
    ? ct.dateRangePreset === 'custom'
      ? `${ct.fromDate || '?'} → ${ct.toDateIsToday ? 'today' : ct.toDate || '?'}`
      : getStatsDateRangeLabel(ct.dateRangePreset as never)
    : 'rule window';
  const pct = ct.percent === undefined ? '' : `${ct.percent}% of `;
  const src = ct.against === 'total' ? 'TOTAL ' : '';
  return `${pct}${src}${labelOf(ct.field)} (${win})`;
}
